/**
 * `array` widget: one element widget per item (whatever the element type),
 * index labels like the reference Inspector's highlights, and add/remove.
 * The whole array is the value, so the binding sees one prop change. Lists
 * that differ across a multi-selection show a disabled dash, as Unreal does
 * for arrays: editing items by position across different lists would guess.
 *
 * The element widget is injected (`createArrayWidget(PropWidget)`), because
 * the widget registry contains this widget: importing it would be a cycle.
 */
import type { FunctionComponent } from "react";
import type { PropType } from "../../world/schema/propTypes";
import { defaultForProp } from "../../world/schema/propTypes";
import { isMixed, MIXED_LABEL } from "./mixed";
import type { WidgetProps } from "./types";

export function createArrayWidget(ItemWidget: FunctionComponent<WidgetProps>) {
  return function ArrayWidget(props: WidgetProps) {
    if (isMixed(props.value)) return <MixedList id={props.id} label={props.label} />;
    return <ArrayItems {...props} ItemWidget={ItemWidget} />;
  };
}

function MixedList({ id, label }: { readonly id: string; readonly label: string }) {
  return (
    <span className="kn-field">
      <input
        id={id}
        type="text"
        className="kn-input"
        aria-label={label}
        value={MIXED_LABEL}
        disabled
      />
    </span>
  );
}

function ArrayItems({
  id,
  label,
  prop,
  value,
  onChange,
  onCommit,
  error,
  ItemWidget,
}: WidgetProps & { readonly ItemWidget: FunctionComponent<WidgetProps> }) {
  const { of, readonly } = prop as Extract<PropType, { type: "array" }>;
  const items: readonly unknown[] = Array.isArray(value) ? value : [];
  const itemProp: PropType = readonly === undefined ? of : { ...of, readonly };

  const replace = (index: number, item: unknown) => {
    onChange(items.map((existing, i) => (i === index ? item : existing)));
  };

  return (
    <span className="kn-array">
      {items.map((item, index) => {
        const itemLabel = `${label} [${index}]`;
        return (
          // Items have no identity beyond their position.
          <span key={index} className="kn-array__item">
            <span className="kn-array__index" aria-hidden="true">
              [{index}]
            </span>
            <ItemWidget
              id={index === 0 ? id : `${id}-${index}`}
              label={itemLabel}
              prop={itemProp}
              value={item}
              onChange={(next) => {
                replace(index, next);
              }}
              onCommit={onCommit}
              error={error ?? null}
            />
            {!readonly && (
              <button
                type="button"
                className="kn-icon-button kn-array__remove"
                aria-label={`Remove ${itemLabel}`}
                title="Remove"
                onClick={() => {
                  onChange(items.filter((_, i) => i !== index));
                  onCommit();
                }}
              >
                ×
              </button>
            )}
          </span>
        );
      })}
      {!readonly && (
        <button
          type="button"
          className="kn-array__add"
          aria-label={`Add to ${label}`}
          onClick={() => {
            onChange([...items, defaultForProp(of)]);
            onCommit();
          }}
        >
          + Add
        </button>
      )}
    </span>
  );
}
